import { KaboodleToaster } from "components/common";
import { useCallback, useState } from "react";
import { useMovePagesWithinMetadocMutation } from "services/metadoc-api";

import { logMetadocDebug } from "../../utils/metadocDebugLogger";
import { convertToPageRanges } from "../../utils/pageRangeUtils";

import type { GroupingSource } from "services/metadoc-api";
import type { PageState } from "../../metadoc.types";

type UsePageMovementParams = {
  metadocId?: number;
  versionHash?: string | null;
  groupingSource?: GroupingSource;
  selectedPages: Set<number>;
  onClearSelection: () => void;
  onSetPageStates?: (states: Map<number, PageState>) => void;
  onMovementStateChange?: (
    isMovingPages: boolean,
    pageCount: number,
    pageRanges: number[][],
    targetPosition: number,
  ) => void;
  onMovementOperationStart?: (meta: {
    pageCount: number;
    pageRanges: number[][];
    targetPosition: number;
  }) => void;
  onMovementOperationJobCreated?: (jobId: number) => void;
  onMovementOperationFail?: (statusMessage?: string) => void;
  onMovementOperationReset?: () => void;
};

type UsePageMovementReturn = {
  isMovingPages: boolean;
  isMoveModalOpen: boolean;
  isSelectingMoveTarget: boolean;
  selectedMoveTarget: number | null;
  openMoveModal: () => void;
  closeMoveModal: () => void;
  startTargetSelection: () => void;
  startTargetSelectionFromModal: () => void;
  cancelTargetSelection: () => void;
  handleTargetSelect: (targetPosition: number) => void;
  handleMoveConfirm: (targetPosition: number) => Promise<void>;
};

/**
 * hook for managing metadoc page move state and interactions
 * handles modal opening/closing, position selection, and movement confirmation
 */
export const usePageMovement = ({
  metadocId,
  versionHash,
  groupingSource = "OPS",
  selectedPages,
  onClearSelection,
  onSetPageStates,
  onMovementStateChange,
  onMovementOperationStart,
  onMovementOperationJobCreated,
  onMovementOperationFail,
  onMovementOperationReset,
}: UsePageMovementParams): UsePageMovementReturn => {
  // modal state
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  // target selection state
  const [isSelectingMoveTarget, setIsSelectingMoveTarget] = useState(false);
  const [selectedMoveTarget, setSelectedMoveTarget] = useState<number | null>(
    null,
  );

  // API mutation for moving pages
  const [movePagesWithinMetadoc, { isLoading: isMoveRequestLoading }] =
    useMovePagesWithinMetadocMutation();

  const isMovingPages = !!isMoveRequestLoading;

  const startTargetSelection = useCallback(() => {
    setIsSelectingMoveTarget(true);
  }, []);

  const startTargetSelectionFromModal = useCallback(() => {
    setIsMoveModalOpen(false);
    setIsSelectingMoveTarget(true);
  }, []);

  const cancelTargetSelection = useCallback(() => {
    setIsSelectingMoveTarget(false);
    setSelectedMoveTarget(null);
  }, []);

  const handleTargetSelect = useCallback((targetPosition: number) => {
    setSelectedMoveTarget(targetPosition);
    setIsSelectingMoveTarget(false);
    setIsMoveModalOpen(true);
  }, []);

  const openMoveModal = useCallback(() => {
    setIsMoveModalOpen(true);
  }, []);

  const closeMoveModal = useCallback(() => {
    setIsMoveModalOpen(false);
    setSelectedMoveTarget(null);
  }, []);

  const handleMoveConfirm = useCallback(
    async (targetPosition: number) => {
      if (!metadocId || !versionHash) {
        return;
      }

      // close modal first (before clearing selection so modal can show correct count)
      setIsMoveModalOpen(false);

      // convert selected pages to page ranges format for API
      const pageRanges = convertToPageRanges(selectedPages);

      // set page states to show which pages are being moved (applies CSS classes)
      if (onSetPageStates) {
        const movementStates = new Map<number, PageState>();
        for (const pageNum of selectedPages) {
          movementStates.set(pageNum, "moving");
        }
        onSetPageStates(movementStates);
      }

      // notify parent immediately so movement screen shows
      onMovementStateChange?.(
        true,
        selectedPages.size,
        pageRanges,
        targetPosition,
      );
      onMovementOperationStart?.({
        pageCount: selectedPages.size,
        pageRanges,
        targetPosition,
      });

      // clear selection immediately
      onClearSelection();

      try {
        logMetadocDebug("page movement requested", {
          metadocId,
          selectedPageCount: selectedPages.size,
          pageRanges,
          targetPosition,
          groupingSource,
        });
        const response = await movePagesWithinMetadoc({
          metadocId,
          sourcePageRanges: pageRanges,
          targetPosition,
          versionHash,
          source: groupingSource,
        }).unwrap();

        logMetadocDebug("page movement background job created", {
          metadocId,
          jobId: response.background_job.id,
        });
        onMovementOperationJobCreated?.(response.background_job.id);
      } catch (error) {
        logMetadocDebug("page movement request failed", {
          metadocId,
        });

        // notify parent of movement state reset
        onMovementStateChange?.(false, 0, [], 0);
        onMovementOperationReset?.();

        // clear page states on error
        if (onSetPageStates) {
          onSetPageStates(new Map());
        }

        // handle version conflict
        const errorStatus = (error as { status?: number })?.status;
        if (errorStatus === 409) {
          KaboodleToaster.error("Version mismatch. Please reload the page.");
          onMovementOperationFail?.(
            "Version mismatch. Please reload the page.",
          );
        } else {
          KaboodleToaster.error("Failed to move pages");
          onMovementOperationFail?.("Failed to move pages");
        }
      }
    },
    [
      metadocId,
      versionHash,
      selectedPages,
      onSetPageStates,
      onMovementStateChange,
      onMovementOperationStart,
      onMovementOperationJobCreated,
      onMovementOperationFail,
      onMovementOperationReset,
      onClearSelection,
      movePagesWithinMetadoc,
    ],
  );

  return {
    isMovingPages,
    isMoveModalOpen,
    isSelectingMoveTarget,
    selectedMoveTarget,
    openMoveModal,
    closeMoveModal,
    startTargetSelection,
    startTargetSelectionFromModal,
    cancelTargetSelection,
    handleTargetSelect,
    handleMoveConfirm,
  };
};
